import * as d3 from "d3";
import React from "react";
import { FaBookOpen, FaCircle } from "react-icons/fa6";
import { lightenHexColor } from "../../utils/helpers";

import { createRoot } from "react-dom/client";

class CreatePieChart {
  svg;
  parent;
  svgG;
  visibleArcG;
  hiddenArcG;
  lineG;
  chartData;
  userData;
  hiddenArcData;
  updateDataCB;
  width;
  height;
  margin = 50;
  padding = { left: 3, right: 2 };
  pieInnerRadius;
  pieOutterRadius;
  pie = d3
    .pie()
    .value((d) => d.value)
    .sort(null);
  arcGenerator;
  currentSegment;
  hiddenArcSvg;
  isViewMode;
  getSelectedArcs;
  selectedArcs = [];
  maxSelectedArcs = 1;
  maxArcPerSide = 1;
  arcHoverEvents;
  isDragging = false;
  arcThickness = 0;
  handleProfileClick;
  polyLinesG;
  titleG;
  isShowLabelOn = false;
  isSmallView = false;
  profileMouseHoverIn;
  profileMouseHoverOut;
  profileOpacityOnMouseHover = 0.5;
  Tooltip;
  isNotDraggable = false;
  prevSwappedArcId = "";
  arcPaddingRadius = 2;
  centerImageBorder = 5;
  segmentHighlighting = false;
  segmentBorder = "#000051";
  segmentOpacity = 0.2;
  segmentLableOpacity = 0.2;
  isDoubleCol = null;
  svgHeightWidth;
  showTooltips = true;
  centerGap = 0;

  emptyChartData = [
    {
      title: "blank",
      value: "1",
      color: "#e1e1e1",
      isBlank: true,
      id: 0,
      position: 10,
    },
  ];

  segmentIconConfig = {
    showOnSegment: "both", //icons,numbers,blank,both
    iconSize: 20,
    dotIconSize: 8,
    numberLableSize: 12,
    minSizeToShowIcons: 0.25,
    minSizeToShowDotIcons: 0.1,
  };

  constructor(
    element,
    data,
    userData,
    updateDataCB,
    isViewMode,
    getSelectedArcs,
    maxSelectedArcs,
    arcHover,
    arcThickness,
    handleProfileClick,
    config,
    margin
  ) {
    this.margin = margin ? margin : 50;
    this.updateDataCB = updateDataCB;
    this.parent = element;
    d3.select(this.parent).selectAll("svg").remove();
    this.svg = d3.select(element).append("svg").attr("id", "chart");
    this.chartData = data;
    this.userData = userData;
    // this.userData = {
    //   id: "1",
    //   url: 'http://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4',
    //   type: "video", // updated
    // };
    this.isViewMode = isViewMode;
    this.getSelectedArcs = getSelectedArcs ? getSelectedArcs : () => {};
    this.maxSelectedArcs = maxSelectedArcs;
    this.updateConfig(config);
    this.arcHoverEvents = !!arcHover ? arcHover : () => {};
    if (arcThickness) {
      this.arcThickness = arcThickness;
    }
    this.handleProfileClick = !!handleProfileClick
      ? handleProfileClick
      : () => {};
    this.showTooltips =
      config?.showTooltips !== undefined ? config.showTooltips : true;
  }
  updateConfig(config) {
    this.maxArcPerSide = config?.maxArcPerSide;
    this.profileMouseHoverIn = config?.profileMouseHoverIn
      ? config?.profileMouseHoverIn
      : () => {};
    this.profileMouseHoverOut = config?.profileMouseHoverOut
      ? config?.profileMouseHoverOut
      : () => {};
    this.profileOpacityOnMouseHover = config?.profileOpacityOnMouseHover;
    this.isSmallView = config?.isSmallView;
    this.isNotDraggable = config?.isNotDraggable;
    this.segmentHighlighting =
      config?.segmentHighlighting === undefined
        ? this.segmentHighlighting
        : config.segmentHighlighting;
    this.segmentOpacity = config?.segmentOpacity || this.segmentOpacity;
    this.segmentBorder = config?.segmentBorder || this.segmentBorder;
    this.centerGap = config?.centerGap ?? 0;
    this.setSvg();

    this.segmentLableLimit = config?.segmentLableLimit || 25;
    this.inactiveOpacity = config?.inactiveOpacity || 0;

    this.arcPaddingRadius = config?.arcPaddingRadius || this.arcPaddingRadius;
    this.centerImageBorder =
      config?.centerImageBorder || this.centerImageBorder;
    this.segmentLableOpacity =
      config?.segmentLableOpacity || this.segmentLableOpacity;
    if (config?.segmentIconConfig) {
      Object.keys(this.segmentIconConfig).forEach((key) => {
        this.segmentIconConfig[key] =
          config.segmentIconConfig[key] || this.segmentIconConfig[key];
      });
    }
    this.showTooltips =
      config?.showTooltips !== undefined
        ? config.showTooltips
        : this.showTooltips;
    this.setSvg();
  }

  resize() {
    this.width = this.parent.clientWidth - this.margin;
    this.height = this.parent.clientHeight - this.margin;
    this.svgHeightWidth = Math.min(this.width, this.height);
    this.svg
      .attr("width", this.svgHeightWidth + this.margin)
      .attr("height", this.svgHeightWidth + this.margin);
    this.pieInnerRadius = this.svgHeightWidth / this.padding.left;
    this.pieOutterRadius = this.svgHeightWidth / this.padding.right;
    const padding =
      this.isSmallView || this.parent.clientHeight - this.margin < 250
        ? this.padding.right
        : this.padding.left + this.padding.right;
    this.pieOutterRadius +=
      this.pieOutterRadius * (this.arcThickness ? this.arcThickness / 100 : 0);
    this.arcGenerator = d3
      .arc()
      .innerRadius(this.pieInnerRadius + padding + this.centerGap)
      .outerRadius((d, i) => {
        return (
          this.pieOutterRadius +
          (d?.data?.selected && !this.segmentHighlighting ? padding * 2 : 0)
        );
      });
    this.Tooltip?.style("opacity", 0)?.style("stroke", "none");
  }

  setMode(isViewMode) {
    this.isViewMode = isViewMode;
  }

  setSvg() {
    this.svg.selectAll("g").remove();
    this.resize();
    this.svgG = this.svg
      .append("g")
      .attr("class", "visible-g")
      .attr(
        "transform",
        `translate(${
          (this.svgHeightWidth + this.margin) / this.padding.right
        },${(this.svgHeightWidth + this.margin) / this.padding.right})`
      );
    this.arcG = this.svgG.append("g").attr("class", "arcs-g");

    this.iconsG = this.svgG.append("g").attr("class", "icon-g");

    this.numberLableG = this.svgG.append("g").attr("class", "number-lable-g");

    this.titleG = this.svgG.append("g").attr("class", "title-text");

    this.polyLinesG = this.svgG.append("g").attr("class", "poly-lines");

    this.lineG = this.svgG.append("g").attr("class", "line-g");

    this.hiddenArcSvg = this.svg
      .append("g")
      .attr("class", "hidden-g")
      .attr(
        "transform",
        `translate(${
          (this.svgHeightWidth + this.margin) / this.padding.right
        },${(this.svgHeightWidth + this.margin) / this.padding.right})`
      );

    this.hiddenArcs = this.hiddenArcSvg.append("g").attr("class", "hidden-arc");

    this.Tooltip = d3
      .select(this.parent)
      .append("div")
      .attr("class", "tooltip")
      .style("position", "fixed") // Add fixed positioning
      .style("z-index", "9999"); // Add high z-index

    this.setProfile(this.userData);
  }

  setProfile(userData) {
    this.userData = userData;
    this.svgG.selectAll(".profile").remove();
    this.svgG.selectAll("defs.patterns").remove();
    const center = this.svgG.append("g").attr("class", "profile");

    if (!this.userData.type || this.userData.type === "image") {
      const patterns = this.svgG
        .selectAll("defs.patterns")
        .data([this.userData])
        .enter()
        .append("svg:defs")
        .attr("class", "patterns")
        .selectAll("pattern")
        .data(function (d, i) {
          return [d];
        })
        .enter()
        .append("svg:pattern")
        .attr("class", "pattern")
        .attr("id", function (d, i) {
          return d.id;
        })
        .attr("x", "0%")
        .attr("y", "0%")
        .attr("height", "100%")
        .attr("width", "100%");

      const image = patterns
        .selectAll(".pattern")
        .data(function (d, i) {
          return [d];
        })
        .enter()
        .append("svg:image")
        .attr("x", "0%")
        .attr("y", "0%")
        .attr("height", this.pieInnerRadius * 2)
        .attr("width", this.pieInnerRadius * 2)
        .attr("xlink:href", (d) => d.url)
        .attr("preserveAspectRatio", "xMidYMid slice")
        .attr("crossOrigin", (d) =>
          d.url?.startsWith("data:image") ? null : "anonymous"
        );

      const onMouseHover = () => {
        centerSvg.style("opacity", this.profileOpacityOnMouseHover);
        this.profileMouseHoverIn();
      };
      const onMouseHoverOut = () => {
        centerSvg.style("opacity", 1);
        this.profileMouseHoverOut();
      };

      const centerSvg = center
        .append("g")
        .on("mouseover", onMouseHover)
        .on("mouseout", onMouseHoverOut);

      const handleClick = () => {
        this.handleProfileClick();
      };

      centerSvg
        .append("circle")
        .attr("r", this.pieInnerRadius)
        .style("fill", "url(#" + this.userData.id + ")")
        .on("click", handleClick)
        .style("cursor", "pointer");

      centerSvg
        .append("circle")
        .attr("r", this.pieInnerRadius + 2)
        .attr("stroke-width", this.centerImageBorder)
        .attr("class", "center-image-border");
    } else if (this.userData.type === "video") {
      const centerSvg = center.append("g");

      // Add clip path
      centerSvg
        .append("defs")
        .append("clipPath")
        .attr("id", "circle-clip")
        .append("circle")
        .attr("r", this.pieInnerRadius);

      // Create foreignObject for video and thumbnail
      const foreignObject = centerSvg
        .append("foreignObject")
        .attr("x", -this.pieInnerRadius)
        .attr("y", -this.pieInnerRadius)
        .attr("width", this.pieInnerRadius * 2)
        .attr("height", this.pieInnerRadius * 2);

      const container = foreignObject
        .append("xhtml:div")
        .style("position", "relative")
        .style("width", "100%")
        .style("height", "100%")
        .style("border-radius", "100%");

      // Add thumbnail image
      const thumbnail = container
        .append("img")
        .attr("src", this.userData.thumbnail) // Replace with your thumbnail URL
        .style("position", "absolute")
        .style("top", 0)
        .style("left", 0)
        .style("width", "100%")
        .style("height", "100%")
        .style("border-radius", "100%")
        .style("object-fit", "cover");

      // Add video element
      const videoElement = container
        .append("video")
        .attr("id", "profileVideo")
        .attr("width", "100%")
        .attr("height", "100%")
        .attr("muted", false)
        .style("position", "absolute")
        .style("top", 0)
        .style("left", 0)
        .style("width", "100%")
        .style("height", "100%")
        .style("border-radius", "100%")
        .style("display", "none");

      videoElement
        .append("source")
        .attr("src", this.userData.url) // sampleVideo
        .attr("type", "video/mp4");

      let playValue = "pause";

      // Create play button
      const playButton = centerSvg
        .append("circle")
        .attr("r", this.pieInnerRadius * 0.2)
        .style("fill", "white")
        .style("opacity", 0)
        .on("mouseover", () => {
          playButton.style("opacity", 1);
          playButtonIcon.style("opacity", 1);
        })
        .on("click", playButtonClick);

      // Create play button icon
      const playButtonIcon = centerSvg
        .append("foreignObject")
        .attr("x", -this.pieInnerRadius * 0.1)
        .attr("y", -this.pieInnerRadius * 0.1)
        .attr("width", this.pieInnerRadius * 0.2)
        .attr("height", this.pieInnerRadius * 0.2)
        .style("opacity", 1)
        .html(
          '<svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 5v14l11-7L8 5z" fill="currentColor"/></svg>'
        ) // FaPlay icon
        .on("click", playButtonClick);

      // Change icon on mouseover
      foreignObject
        .on("mouseover", () => {
          playButton.style("opacity", 1);
          playButtonIcon.style("opacity", 1);
          setTimeout(() => {
            const iconHtml =
              playValue === "pause"
                ? '<svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 5v14l11-7L8 5z" fill="currentColor"/></svg>' // FaPlay icon
                : '<svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 19h4.5V5H6v14zm7.5-14v14H18V5h-4.5z" fill="currentColor"/></svg>'; // FaPause icon
            playButtonIcon.html(iconHtml);
          });
        })
        .on("mouseout", () => {
          playButton.style("opacity", 0);
          playButtonIcon.style("opacity", 0);
        });

      function playButtonClick() {
        const video = videoElement.node();
        if (playValue === "pause") {
          playValue = "play";
          thumbnail.style("display", "none"); // Hide thumbnail
          videoElement.style("display", "block"); // Show video
          video.play();
          setTimeout(() => {
            playButtonIcon.html(
              '<svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 19h4.5V5H6v14zm7.5-14v14H18V5h-4.5z" fill="currentColor"/></svg>'
            ); // FaPause icon
          });
        } else {
          playValue = "pause";
          video.pause();
          setTimeout(() => {
            playButtonIcon.html(
              '<svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 5v14l11-7L8 5z" fill="currentColor"/></svg>'
            ); // FaPlay icon
          });
        }
      }
    }
  }

  updateArcs(data) {
    this.chartData = data;
    if (!this.chartData?.length) {
      this.chartData = this.emptyChartData;
    }
    this.selectedArcs = [];
    this.pie(this.chartData).forEach((dd) => {
      if (dd.data.selected) this.selectedArcs.push(dd.data.id);
    });
    this.resize();
    const arcs = this.arcG
      .selectAll(".arc")
      .data(this.pie(this.chartData), (d) => d.data.id);

    arcs.exit().remove();

    this.updateImage();

    arcs
      .enter()
      .append("path")
      .attr("class", "arc")
      .attr("d", this.arcGenerator)
      .style("fill", (d) => {
        return d.data.pattern ? `url(#p${d.data.id})` : d.data.color;
      })
      .style("stroke-width", this.arcPaddingRadius)
      .merge(arcs)
      .style("stroke", (d) => {
        if (this.segmentHighlighting && this.selectedArcs.length) {
          if (this.selectedArcs.find((item) => item === d.data.id)) {
            return this.segmentBorder;
          }
        }
      })
      .style("opacity", (d) => {
        if (this.segmentHighlighting && this.selectedArcs.length) {
          if (!this.selectedArcs.find((item) => item === d.data.id)) {
            return this.segmentOpacity;
          }
        }
      })
      .transition()
      .duration(this.chartData.length > 2 ? 200 : 0)
      .ease(d3.easeLinear)
      .attr("d", this.arcGenerator)
      .style("fill", (d) => {
        return d.data.pattern ? `url(#p${d.data.id})` : d.data.color;
      });
    // this.updateIcons();
    // this.updateNumberLable();
    this.mapDndChartData();
    if (this.isShowLabelOn) {
      this.titleG.selectAll("text").remove();
      this.titleG.selectAll("g").remove();
      this.polyLinesG.selectAll("polyline").remove();
      this.lineG.selectAll("line").remove();
      // this.updatedTitle();
      this.updateLable();
    }
  }

  updateImage() {
    this.arcG.selectAll("defs").remove();
    this.arcG
      .selectAll("images")
      .data(this.pie(this.chartData), (d) => d.data.id)
      .enter()
      .append("defs")
      .append("pattern")
      .attr("id", (d, i) => `p${d.data.id}`)
      .attr("x", "0%")
      .attr("y", "0%")
      .attr("height", "100%")
      .attr("width", "100%")
      .append("image")
      .attr("xlink:href", (d) => d.data.pattern)
      .attr("x", "0%")
      .attr("y", "0%")
      .attr("height", this.pieOutterRadius * 2.5)
      .attr("width", this.pieOutterRadius * 2.5)
      .attr("preserveAspectRatio", "xMidYMid slice");
  }

  // Icons on segment
  updateIcons() {
    this.iconsG.selectAll("foreignObject").remove();
    if (
      this.segmentIconConfig.showOnSegment === "icons" ||
      this.segmentIconConfig.showOnSegment === "both"
    ) {
      const iconsG = this.iconsG
        .selectAll(".icons-g")
        .data(this.pie(this.chartData), (d) => d.data.id);

      // Dot icon
      const dotIconContainer = document.createElement("div");
      const root = createRoot(dotIconContainer);
      root.render(<FaCircle />);
      setTimeout(() => {
        const dotIconHtml = dotIconContainer.innerHTML;
        iconsG
          .enter()
          .append("foreignObject")
          .attr("class", "icons")
          .attr("transform", (d, i) => {
            let pos = this.arcGenerator.centroid(d);
            const iconSize =
              this.segmentIconConfig.minSizeToShowIcons >
              d.endAngle - d.startAngle
                ? this.segmentIconConfig.dotIconSize
                : this.segmentIconConfig.iconSize;
            return `translate(${pos[0] - iconSize / 2},${
              pos[1] - iconSize / 2
            })`;
          })
          .attr("width", (d) => {
            return this.segmentIconConfig.minSizeToShowIcons >
              d.endAngle - d.startAngle
              ? this.segmentIconConfig.dotIconSize
              : this.segmentIconConfig.iconSize;
          })
          .attr("height", (d) => {
            return this.segmentIconConfig.minSizeToShowIcons >
              d.endAngle - d.startAngle
              ? this.segmentIconConfig.dotIconSize
              : this.segmentIconConfig.iconSize;
          })
          .each((d, i, nodes) => {
            const Icon = FaBookOpen; // demo icon
            // const icon = d.data.icon;
            if (Icon) {
              const container = document.createElement("div");
              // ReactDOM.render(<Icons />, container);
              setTimeout(() => {
                const iconHtml = container.innerHTML;
                if (
                  this.segmentIconConfig.minSizeToShowDotIcons >
                  d.endAngle - d.startAngle
                ) {
                  nodes[i].innerHTML = "";
                } else if (
                  this.segmentIconConfig.minSizeToShowIcons >
                  d.endAngle - d.startAngle
                ) {
                  nodes[i].innerHTML = this.changeIconSvgColor(
                    dotIconHtml,
                    this.getColorContrast(d.data.color)
                  );
                } else {
                  nodes[i].innerHTML = this.changeIconSvgColor(
                    iconHtml,
                    this.getColorContrast(d.data.color)
                  );
                }
              });
            }
          });
      });
    }
  }

  // Number lable on segment
  updateNumberLable() {
    this.numberLableG.selectAll("text").remove();
    if (
      this.segmentIconConfig.showOnSegment === "numbers" ||
      this.segmentIconConfig.showOnSegment === "both"
    ) {
      const numberLableG = this.numberLableG
        .selectAll(".numberLable-g")
        .data(this.pie(this.chartData), (d) => d.data.id);
      numberLableG
        .join("text")
        .style("fill", (d) => this.getColorContrast(d.data.color))
        .style("font-size", this.segmentIconConfig.numberLableSize * 0.75)
        .attr("dy", "0.35em")
        .attr("transform", (d) => {
          if (this.segmentIconConfig.showOnSegment === "both") {
            const rotation =
              d.endAngle < Math.PI
                ? (d.endAngle * 180) / Math.PI
                : ((d.endAngle + Math.PI) * 180) / Math.PI;
            const angle = d.endAngle - 0.05 - Math.PI / 2;
            const padding =
              this.isSmallView || this.parent.clientHeight - this.margin < 250
                ? this.padding.right
                : this.padding.left + this.padding.right;

            const radius =
              this.pieOutterRadius +
              (d?.data?.selected && !this.segmentHighlighting
                ? padding * 2
                : 0);

            const x = Math.cos(angle) * (radius - 2);
            const y = Math.sin(angle) * (radius - 2);
            return `translate(${x}, ${y}) rotate(-90) rotate(${rotation})`;
          } else {
            const rotation =
              d.endAngle <= Math.PI
                ? ((d.startAngle / 2 + d.endAngle / 2) * 180) / Math.PI
                : ((d.startAngle / 2 + d.endAngle / 2 + Math.PI) * 180) /
                  Math.PI;
            return `translate(${this.arcGenerator.centroid(d)}) , rotate(${
              rotation - 90
            })`;
          }
        })
        .text((d) => {
          if (
            this.segmentIconConfig.minSizeToShowDotIcons <
            d.endAngle - d.startAngle
          ) {
            return this.formatNumberLable(d.data.value);
          }
        })
        .attr("text-anchor", (d) => {
          if (this.segmentIconConfig.showOnSegment === "both") {
            return d.endAngle >= Math.PI ? "start" : "end";
          } else {
            return "middle";
          }
        });
    }
  }

  formatNumberLable(num) {
    if (num >= 1000000) {
      let millions = num / 1000000;
      return millions % 1 === 0 ? millions + "M" : millions.toFixed(1) + "M";
    } else if (num >= 1000) {
      let thousands = num / 1000;
      return thousands % 1 === 0 ? thousands + "K" : thousands.toFixed(1) + "K";
    } else {
      return num.toString();
    }
  }

  // Fetch the svg icon data and change the color
  changeIconSvgColor(svgString, color) {
    const parser = new DOMParser();
    const element = parser.parseFromString(svgString, "image/svg+xml");
    const iconElement = element.querySelector("svg");
    if (iconElement) {
      iconElement.setAttribute("width", `100%`);
      iconElement.setAttribute("height", `100%`);
      iconElement.style.display = "flex";
    }

    const setStrokeColor = (tag, tagColor) => {
      const path = element.querySelectorAll(tag);
      if (path.length) {
        path.forEach((d) => {
          d.setAttribute("stroke", tagColor);
        });
      }
    };

    setStrokeColor("path", color);
    setStrokeColor("polygon", color);
    setStrokeColor("circle", color);
    setStrokeColor("ellipse", color);
    setStrokeColor("line", color);
    setStrokeColor("polyline", color);
    setStrokeColor("rect", color);
    setStrokeColor("text", color);
    setStrokeColor("line", color);
    // const path = element.querySelector('path');
    // if (path) {
    //   path.setAttribute('stroke', color);
    //   path.setAttribute('fill', color);
    // }

    // const polygon = element.querySelector('polygon');
    // if (polygon) polygon.setAttribute('stroke', color);

    // const circle = element.querySelector('circle');
    // if (circle) circle.setAttribute('stroke', color);

    // const ellipse = element.querySelector('ellipse');
    // if (ellipse) ellipse.setAttribute('stroke', color);

    // const line = element.querySelector('line');
    // if (line) line.setAttribute('stroke', color);

    // const polyline = element.querySelector('polyline');
    // if (polyline) polyline.setAttribute('stroke', color);

    // const rect = element.querySelector('rect');
    // if (rect) rect.setAttribute('stroke', color);

    // const text = element.querySelector('text');
    // if (text) text.setAttribute('stroke', color);

    const serializer = new XMLSerializer();
    return serializer.serializeToString(element);
  }

  // Get the color contrast
  getColorContrast(hex) {
    if (!hex || hex == null || hex == undefined) return "";
    let threshold = 200;
    let hRed = hexToR(hex);
    let hGreen = hexToG(hex);
    let hBlue = hexToB(hex);

    function hexToR(h) {
      return parseInt(cutHex(h)?.substring(0, 2), 16);
    }
    function hexToG(h) {
      return parseInt(cutHex(h)?.substring(2, 4), 16);
    }
    function hexToB(h) {
      return parseInt(cutHex(h)?.substring(4, 6), 16);
    }
    function cutHex(h) {
      return h.charAt(0) == "#" ? h?.substring(1, 7) : h;
    }

    let cBrightness = (hRed * 299 + hGreen * 587 + hBlue * 114) / 1000;
    if (cBrightness > threshold) {
      return "#00008B";
    } else {
      return "#ffffff";
    }
  }

  manageLabels(event) {
    this.isShowLabelOn = event;
    if (this.isShowLabelOn) {
      // this.updatedTitle();
      this.updateLable();
    } else {
      this.titleG.selectAll("text").remove();
      this.titleG.selectAll("g").remove();
      this.polyLinesG.selectAll("polyline").remove();
      this.lineG.selectAll("line").remove();
    }
  }

  updateLable() {
    if (this.chartData.length && this.chartData[0].isBlank) return;
    const self = this;
    const arc = d3
      .arc()
      .innerRadius(this.pieOutterRadius)
      .outerRadius(this.pieOutterRadius);

    self.titleG.selectAll("g").remove();
    self.lineG.selectAll("line").remove();
    this.lineG
      .selectAll("line")
      .data(
        this.pie(this.chartData).filter((d) => d.data.title.trim() !== ""),
        (d) => d.data.id
      ) // Filter out empty labels
      .join("line")
      .attr("id", (d) => `l${d.data.id}`)
      .style("stroke", (d) => d.data.color)
      .style("stroke-width", 2)
      .style("opacity", 1) // Fully opaque by default
      .attr("x1", (d) => arc.centroid(d)[0])
      .attr("y1", (d) => arc.centroid(d)[1])
      .attr("x2", (d) => arc.centroid(d)[0] * 1.15)
      .attr("y2", (d) => arc.centroid(d)[1] * 1.15);

    const labelGroup = this.titleG
      .selectAll("labels")
      .data(
        this.pie(this.chartData).filter((d) => d.data.title.trim() !== ""),
        (d) => d.data.id
      ) // Filter out empty labels
      .enter()
      .append("g")
      .attr("id", (d, i) => `b${d.data.id}`)
      .style("opacity", 1) // Fully opaque by default
      .attr("transform", function (d) {
        var pos = arc.centroid(d);
        var midangle = d.startAngle + (d.endAngle - d.startAngle) / 2;
        pos[0] = this.pieOutterRadius * 0.99 * (midangle < Math.PI ? 1 : -1);
        return `translate(${
          arc.centroid(d)[0] * 1.2
        },${arc.centroid(d)[1] * 1.2})`;
      })
      .attr("class", "label-group");

    labelGroup
      .append("text")
      .attr("x", 0)
      .attr("y", 0)
      .text(function (d) {
        return d.data.title.length > self.segmentLableLimit
          ? d.data.title.slice(0, self.segmentLableLimit) + "..."
          : d.data.title;
      })
      .style("text-anchor", (d) => {
        const midAngle = d.startAngle + (d.endAngle - d.startAngle) / 2;
        if (midAngle === Math.PI) return "middle";
        else return midAngle < Math.PI ? "start" : "end";
      })
      .style("font-size", "12px")
      .attr("dy", "0.35em")
      .attr("class", "rect-text")
      .style("fill", (d) => this.getColorContrast(d.data.color))
      .style("cursor", "pointer")
      .on("mouseover", function (event, d, i) {
        self.highlighLabels(d.data, labelGroup, self, true);
      })
      .on("mouseleave", function (event, d) {
        self.highlighLabels(d.data, labelGroup, self, false);
      })
      .on("click", function (event, d) {
        // Simulate a segment click when the label is clicked
        self.arcSelected(event, d); // Call the arcSelected method
      });
    self.loadRectSize(labelGroup, self);
  }

  highlighLabels(data, labelGroup, self, isMouseHovered) {
    const setOpacity = (targetOpacity, isSliceText) => {
      self.lineG.select(`#l${data.id}`).style("opacity", targetOpacity);
      self.titleG
        .select(`#b${data.id}`)
        .style("opacity", targetOpacity)
        .select("text")
        .text((d) =>
          data.title.length > 25 && isSliceText
            ? data.title.slice(0, 25) + "..."
            : data.title
        );
      self.titleG.selectAll("rect").remove();
      self.loadRectSize(labelGroup, self);
    };

    if (isMouseHovered) {
      setOpacity(1, false);
      labelGroup.each(function (d) {
        if (d.data.id !== data.id) {
          self.titleG
            .select(`#b${d.data.id}`)
            .style("opacity", self.inactiveOpacity);
          self.lineG
            .select(`#l${d.data.id}`)
            .style("opacity", self.inactiveOpacity); // Apply opacity to lines
        }
      });
    } else {
      labelGroup.each(function (d) {
        self.titleG.select(`#b${d.data.id}`).style("opacity", 1);
        self.lineG.select(`#l${d.data.id}`).style("opacity", 1);
      });
      setOpacity(1, true);
    }
  }

  loadRectSize(labelGroup, self = null) {
    // Calculate the size of the text and then adjust the rectangles accordingly
    labelGroup.each(function (d) {
      const textElement = self.titleG.select(`#b${d.data.id}`).select("text");
      const bbox = textElement.node().getBBox();
      const padding = 12;

      // Add the rectangles based on the text's bounding box size
      self.titleG
        .select(`#b${d.data.id}`)
        .insert("rect", "text")
        .attr("x", bbox.x - padding)
        .attr("y", bbox.y - padding * 0.4)
        .attr("width", bbox.width + 2 * padding)
        .attr("height", bbox.height + 2 * (padding * 0.4))
        .attr("class", "rect-label")
        .style("fill", d.data.color)
        .attr("rx", 15);
    });
  }

  spliceString(string, isContinue) {
    const self = this;
    if (string?.length <= 25 && isContinue) {
      return;
    }
    let trimLength =
      string.length > 25 && string.length <= 50
        ? Math.floor(string.length / 2)
        : 25;
    if (!isContinue && string?.length <= 25) {
      return string;
    } else {
      if (!self.isDoubleCol && string?.length >= trimLength) {
        const spaceIndex = self.findNextSpaceIndex(string, trimLength);
        if (spaceIndex && spaceIndex !== -1) {
          trimLength = spaceIndex + 1;
        } else {
          trimLength = trimLength;
        }
      }
      if (isContinue) {
        if (string?.length <= 50) {
          return string?.substring(trimLength, 50);
        } else {
          return string?.substring(trimLength - 1, 50 - 3) + "...";
        }
      } else {
        if (!self.isDoubleCol && string?.length >= trimLength) {
          return string?.substring(0, trimLength);
        } else {
          return string?.substring(0, 25 - 3) + "...";
        }
      }
    }
  }

  findNextSpaceIndex(inputString, startIndex) {
    let i1, i2;
    for (let i = startIndex; i < inputString.length; i++) {
      if (inputString.charAt(i) === " ") {
        i1 = i;
        break;
      }
    }
    for (let i = startIndex; i >= 0; i--) {
      if (inputString.charAt(i) === " ") {
        i2 = i;
        break;
      }
    }
    return i1 - startIndex > startIndex - i2 ? i2 : i1;
  }

  mouseover(d, i, text) {
    const self = this;
    d3.select(text).style("opacity", 0.2);

    self.Tooltip.style("opacity", 1)
      .style("stroke", "black")
      .style("opacity", 1);
  }

  mousemove(d, i, node) {
    const self = this;
    const tooltipWidth = self.Tooltip.node().offsetWidth;
    // const tooltipHeight = self.Tooltip.node().offsetHeight;
    const x1 = d.layerX - tooltipWidth / 2; //pageX
    const y1 = d.layerY - 10; //pageY
    const nodeRef = d3.select(node).node().getBoundingClientRect();
    const xPos =
      !self.isDoubleCol && self.spliceString(i.data.title) != i.data.title
        ? x1 - (x1 + nodeRef.width / 2 - x1)
        : x1;
    const yPos =
      !self.isDoubleCol && self.spliceString(i.data.title) != i.data.title
        ? y1
        : y1 - 5;
    const x = xPos;
    const y = yPos;
    self.Tooltip.style(
      "background-color",
      (d) => i?.data?.inner_color || i?.data?.color
    );
    self.Tooltip.text(i.data.title)
      .style("left", x + "px")
      .style("top", y + "px");
  }

  mouseleave(d, i, text) {
    const self = this;
    d3.select(text).style("opacity", 1);

    self.Tooltip.style("opacity", 0).style("stroke", "none");
  }

  updatedTitle() {
    if (this.chartData.length && this.chartData[0].isBlank) return;
    const self = this;
    self.isDoubleCol = self.chartData.length > 20;
    const extraSpacing = self.isDoubleCol ? 30 : 0;
    const arcLabel = d3
      .arc()
      .innerRadius(this.pieOutterRadius * 1)
      .outerRadius(this.pieOutterRadius * 1.2);

    const midAngle = (d) => d.startAngle + (d.endAngle - d.startAngle) / 2;

    const label_position = (d, i) => {
      let pos = arcLabel.centroid(d);
      const textPosition = self.isDoubleCol
        ? 0
        : self.spliceString(d.data.title).length * 3;
      pos[0] =
        self.pieOutterRadius * (midAngle(d) < Math.PI ? 1 : -1) +
        (midAngle(d) < Math.PI ? self.margin / 2 : -(self.margin / 2));
      if (self.isDoubleCol && (i || i == 0)) {
        if (i % 2 == 0) {
          pos[0] =
            pos[0] -
            extraSpacing +
            (midAngle(d) < Math.PI ? 0 : -35) +
            textPosition;
        } else {
          pos[0] =
            pos[0] +
            extraSpacing +
            (midAngle(d) < Math.PI ? 35 : 0) +
            textPosition;
        }
      }
      return pos;
    };

    const label_anchor = (d) => {
      return midAngle(d) < Math.PI ? "start" : "end";
    };

    const arcLine = d3
      .arc()
      .innerRadius(this.pieOutterRadius)
      .outerRadius(this.pieOutterRadius * 0.8);

    const line_position = (d, i) => {
      let arcLabels = arcLabel.centroid(d);
      let pos = [];
      pos[0] =
        self.pieOutterRadius * 1 * (midAngle(d) < Math.PI ? 1 : -1) +
        (midAngle(d) < Math.PI ? self.margin / 2 : -(self.margin / 2));
      pos[1] = arcLabels[1];
      if (self.isDoubleCol && (i || i == 0)) {
        if (i % 2 == 0) {
          pos[0] = pos[0] - extraSpacing + (midAngle(d) < Math.PI ? 0 : -35);
        } else {
          pos[0] = pos[0] + extraSpacing + (midAngle(d) < Math.PI ? 35 : 0);
        }
      }
      return [arcLine.centroid(d), arcLabels, pos];
    };
    this.titleG.exit().remove();
    this.polyLinesG.exit().remove();

    this.titleG
      .selectAll("text")
      .data(this.pie(this.chartData), (d) => d.data.id)
      .join((enter) =>
        enter.append("text").call((selection) =>
          selection
            .attr("dy", ".35em")
            .style("cursor", "pointer")
            .style("font-size", "12px")
            .attr("transform", (d, i) => `translate(${label_position(d, i)})`)
            .style("text-anchor", (d) => label_anchor(d))
            .call((text) =>
              text
                .append("tspan")
                .text((d) => self.spliceString(d.data.title, false))
                .attr("dy", (d) =>
                  !self.isDoubleCol &&
                  self.spliceString(d.data.title) != d.data.title
                    ? "-0.3em"
                    : ""
                )
                .attr("x", (d) => (label_anchor(d) === "start" ? 10 : -10))
            )
            .call((text) =>
              text
                .append("tspan")
                .text((d) =>
                  !self.isDoubleCol ? self.spliceString(d.data.title, true) : ""
                )
                .attr("dy", "1em")
                .attr("x", (d) => (label_anchor(d) === "start" ? 10 : -10))
            )
            .on("mouseover", function (event, d) {
              self.mouseover(event, d, this);
            })
            .on("mousemove", function (d, i) {
              self.mousemove(d, i, this);
            })
            .on("mouseleave", function (event, d) {
              d3.select(this).style("fill", null);
              self.mouseleave(event, d, this);
            })
            .on("click", self.arcSelected.bind(this))
        )
      );

    this.polyLinesG
      .selectAll("polyline")
      .data(this.pie(this.chartData), (d) => d.data.id)
      .join((enter) =>
        enter
          .append("polyline")
          .attr("class", "polyline")
          .style("stroke", (d) => d.data.color)
          .call((selection) =>
            selection.attr("points", (d, i) => {
              return line_position(d, i);
            })
          )
      );
  }

  mapDndChartData() {
    // Create a copy of chartData to avoid mutation issues
    const chartData = [...this.chartData].sort(
      (a, b) => a.position - b.position
    );
    const mappedData = [];
    chartData.forEach((d, i) => {
      const rightSideArc = { ...d };
      const leftSideArc = { ...d };
      rightSideArc.key = `right_${d.id}`;
      rightSideArc.custom_position = i * 10;
      mappedData.push({ ...rightSideArc });
      leftSideArc.key = `left_${d.id}`;
      leftSideArc.custom_position = rightSideArc.custom_position + 10;
      mappedData.push({ ...leftSideArc });
    });
    mappedData.sort((a, b) => a.custom_position - b.custom_position);
    this.setHiddenArc(mappedData);
  }

  dragstart(d, i) {
    if (i?.data?.isBlank) return;
    this.isDragging = true;
    if (this.isViewMode || this.isNotDraggable) {
      return;
    }
    this.svgG.selectAll("path").style("opacity", (dd, ii) => {
      // return i.data?.id == dd.data?.id ? 1 : 0.3;
    });
    this.prevSwappedArcId = "";
  }

  drag(d, index) {
    setTimeout(() => {
      if (this.isViewMode || this.isNotDraggable || index?.data?.isBlank) {
        return;
      }
      if (
        this.currentSegment !== null &&
        index.data.key != this.currentSegment.key
      ) {
        if (this.prevSwappedArcId != this.currentSegment?.key) {
          let newOrder;
          const selectedSegIndex = this.chartData.findIndex(
            (d) => d.id == index.data.id
          );
          const mapData = this.chartData.map((dd, i) => {
            if (dd.id == this.currentSegment.id) {
              const isAfter =
                this.currentSegment.key.includes("right") &&
                selectedSegIndex + 1 == i;
              const isBefore =
                this.currentSegment.key.includes("left") &&
                selectedSegIndex - 1 == i;

              if (
                (this.currentSegment.key.includes("left") && !isBefore) ||
                isAfter
              ) {
                newOrder = dd.position + 1;
              } else if (
                selectedSegIndex != this.chartData.length - 1 ||
                isBefore
              ) {
                newOrder = dd.position - 1;
              }

              if (
                (selectedSegIndex == this.chartData.length - 1 &&
                  i == 0 &&
                  !this.currentSegment.key.includes("left")) ||
                (selectedSegIndex == 0 &&
                  i == this.chartData.length - 1 &&
                  !this.currentSegment.key.includes("right"))
              ) {
                newOrder = dd.position;
                dd.position = this.chartData[selectedSegIndex].position;
              }
            }
            return dd;
          });
          mapData[selectedSegIndex].position = newOrder;
          mapData.sort((a, b) => a.position - b.position);
          const updatedData = mapData?.map((d, i) => {
            d.position = i * 10;
            return d;
          });
          updatedData.sort((a, b) => a.position - b.position);
          this.updateDataCB(updatedData);
          this.prevSwappedArcId = this.currentSegment?.key ?? "";
        }
      }
    });
  }

  dragEnd(d, i) {
    if (i?.data?.isBlank) return;
    if (this.isShowLabelOn) {
      this.titleG.selectAll("text").remove();
      this.titleG.selectAll("g").remove();
      this.polyLinesG.selectAll("polyline").remove();
      this.lineG.selectAll("line").remove();
      // this.updatedTitle();
      this.updateLable();
    }
    this.svgG.selectAll("path").style("opacity", (d) => {
      if (this.segmentHighlighting && this.selectedArcs.length) {
        if (!this.selectedArcs.find((item) => item === d.data.id)) {
          return this.segmentOpacity;
        }
      }
    });
    this.prevSwappedArcId = "";
    this.isDragging = false;
  }

  arcSelected(event, d) {
    if (d?.data?.isBlank) return;
    if (
      this.chartData.filter(
        (arcData) =>
          arcData.selected && arcData.isPinned && arcData.id !== d.data.id
      )?.length == 2
    ) {
      return;
    }
    this.selectedArcs = this.selectedArcs.filter((d) =>
      this.chartData.find((u) => u.id === d)
    );
    this.chartData.forEach((d) =>
      !this.selectedArcs.includes(d.id) && d.selected
        ? this.selectedArcs.push(d.id)
        : this.selectedArcs
    );

    this.selectedArcs.forEach((arcs, i) => {
      const index = this.chartData.findIndex((arc) => arc.id == arcs);
      if (index > -1 && !this.chartData[index].selected) {
        this.selectedArcs.splice(i, 1);
      }
    });

    if (this.isViewMode) {
      const arcsData = this.setSelectedArcSides(true);
      const isExist = arcsData.find((arc) => arc.id == d.data.id);

      if (arcsData?.length >= this.maxArcPerSide && !isExist) {
        const rightCount = arcsData.filter((d) => d.side == "right")?.length;
        const leftCount = arcsData.filter((d) => d.side == "left")?.length;

        const midpointAngle = (d.startAngle + d.endAngle) / 2;
        let side;
        if (midpointAngle == Math.PI) {
          side = "left";
        } else if (midpointAngle > Math.PI) {
          side = "left";
        } else {
          side = "right";
        }

        if (rightCount >= this.maxArcPerSide && side == "right") {
          const firstIndex = this.selectedArcs.findIndex((sArc) => {
            return arcsData.find(
              (arc) => arc.id == sArc && arc.side == "right"
            );
          });
          this.chartData = this.chartData.map((arc) => {
            if (arc.id == this.selectedArcs[firstIndex]) arc.selected = false;
            return arc;
          });
          this.selectedArcs.splice(firstIndex, 1);
        } else if (leftCount >= this.maxArcPerSide && side == "left") {
          const firstIndex = this.selectedArcs.findIndex((sArc) => {
            return arcsData.find((arc) => arc.id == sArc && arc.side == "left");
          });
          this.chartData = this.chartData.map((arc) => {
            if (arc.id == this.selectedArcs[firstIndex]) arc.selected = false;
            return arc;
          });
          this.selectedArcs.splice(firstIndex, 1);
        }
      }
    }
    let isHavingPinned = 0;
    let isArcSelectedChanged = false;
    const updatedData = this.chartData.map((arcData) => {
      if (arcData.id === d.data.id) {
        if (arcData.selected && arcData.isPinned) {
          arcData.isPinned = !arcData.isPinned;
        }
        arcData.selected = !arcData.selected;
        if (arcData.selected) {
          this.selectedArcs.push(arcData.id);
          isArcSelectedChanged = arcData.selected;
        }
      }
      if (arcData.isPinned) {
        isHavingPinned += 1;
      }
      return arcData;
    });
    if (isArcSelectedChanged) {
      if (
        this.selectedArcs.length > this.maxSelectedArcs &&
        !(isHavingPinned == 1 && this.selectedArcs.length == 2) &&
        !this.isViewMode
      ) {
        for (let i = 0; i < this.selectedArcs?.length; i++) {
          const index = updatedData.findIndex(
            (d) => d.id == this.selectedArcs[i]
          );
          const data = updatedData[index];
          if (data && !data?.isPinned) {
            updatedData[index].selected = false;
            this.selectedArcs.splice(i, 1);
            break;
          }
        }
      }
    } else {
      const index = this.selectedArcs.findIndex((ar) => ar == d.data.id);
      this.selectedArcs.splice(index, 1);
    }
    this.setSelectedArcSides();
    this.updateDataCB(updatedData);
  }

  setSelectedArcSides(getData) {
    let arcsData = [];
    const arcsAngles = this.pie(this.chartData);
    this.chartData.forEach((element) => {
      if (element.selected) {
        const arc = { id: element.id, side: "" };
        const arcAngle = arcsAngles.find((d) => d.data.id == element.id);
        if (arcAngle) {
          const midpointAngle = (arcAngle.startAngle + arcAngle.endAngle) / 2;
          if (midpointAngle == Math.PI) {
            arc.side = "left"; //was middle before
          } else if (midpointAngle > Math.PI) {
            arc.side = "left";
          } else {
            arc.side = "right";
          }
        }
        arcsData.push({ ...arc, ...element, arcAngle });
      }
    });
    if (getData) {
      return arcsData;
    } else {
      this.getSelectedArcs(arcsData);
    }
  }

  pie_in(d, i) {
    if (i?.data?.isBlank) return;
    this.currentSegment = i?.data;
    if (!this.isDragging) {
      const hoveredData = this.chartData.find(
        (data) => data.id == this.currentSegment.id
      );
      this.arcHoverEvents(hoveredData);
    }
  }

  pie_out(d, i) {
    this.currentSegment = null;
    if (!this.isDragging) {
      this.arcHoverEvents(null);
    }
  }

  setHiddenArc(mappedData) {
    this.hiddenArcs.exit().remove();

    const self = this;
    const mouseover = (d, i) => {
      if (i?.data?.isBlank) return;
      self.pie_in(d, i);

      if (!self.isDragging && self.showTooltips) {
        if (this.isShowLabelOn) {
          self.highlighLabels(i.data, d3.selectAll(".label-group"), self, true);
        } else {
          self.Tooltip.style("opacity", () => {
            if (this.segmentHighlighting && this.selectedArcs.length) {
              if (!this.selectedArcs.find((item) => item === i.data.id))
                return 0;
            }
            return 1;
          }).style("stroke", "black");
        }
      }
    };

    const mousemove = (d, i) => {
      if (i?.data?.isBlank || this.isShowLabelOn || !self.showTooltips) return;
      const tooltipWidth = self.Tooltip.node().offsetWidth;
      const tooltipHeight = self.Tooltip.node().offsetHeight;
      const x = d.clientX - tooltipWidth / 2; //pageX
      const y = d.clientY - tooltipHeight - 10; //pageY
      // self.Tooltip.style("background-color", "yellow");
      self.Tooltip.style(
        "background-color",
        i?.data?.color ? lightenHexColor(i?.data?.color, 20) : ""
      );

      self.Tooltip.text(i?.data?.title)
        .style("left", x + "px")
        .style("top", y + "px");
    };

    const mouseleave = (d, i) => {
      if (i?.data?.isBlank) return;

      if (self.showTooltips) {
        if (this.isShowLabelOn) {
          self.highlighLabels(
            i.data,
            d3.selectAll(".label-group"),
            self,
            false
          );
        } else {
          self.Tooltip.style("opacity", 0).style("stroke", "none");
        }
      }
    };

    this.hiddenArcs
      .selectAll("path")
      .data(this.pie([...mappedData]), (d) => d.data.id)
      .join(
        (enter) =>
          enter.append("path").call((selection) => {
            selection
              .call(
                d3
                  .drag()
                  .on("start", this.dragstart.bind(this))
                  .on("drag", this.drag.bind(this))
                  .on("end", this.dragEnd.bind(this))
              )
              .style("cursor", "pointer")
              .style("fill", "transparent")
              .attr("id", (d) => d.data.key)
              .attr("d", this.arcGenerator)
              .on("mouseover", mouseover)
              .on("mouseout", self.pie_out.bind(this))
              .on("click", self.arcSelected.bind(this))
              .on("mousemove", mousemove)
              .on("mouseleave", mouseleave);
          }),
        (update) =>
          update.call((selection) => selection.attr("d", this.arcGenerator))
      );
  }
}

export default CreatePieChart;
